import { t, countryName } from '../i18n/index.js';
import { flagEmoji } from '../domain/flagEmoji.js';
import { chartPenetration, unchartedMarkets } from '../domain/marketGap.js';

/**
 * Render the Market Gap Scanner panel into `container`.
 *
 * Shows chart penetration counts and top-5 notable gaps (major markets not charting).
 * Hidden when chart data is not loaded (no row has a non-null chartRankFree/Paid/Grossing).
 *
 * @param {Array<{countryCode: string, available?: boolean, chartRankFree?: number|null, chartRankPaid?: number|null, chartRankGrossing?: number|null}>} rows
 * @param {HTMLElement|null} container
 */
export function renderMarketGap(rows, container) {
  if (!container) return;

  const pen = chartPenetration(rows);

  // Hide if chart enrichment wasn't run (no charting data at all).
  if (pen.any === 0 && pen.total > 0 && pen.free === 0 && pen.paid === 0 && pen.grossing === 0) {
    const hasAnyChartData = rows.some(
      (r) => r.chartRankFree !== undefined || r.chartRankPaid !== undefined || r.chartRankGrossing !== undefined,
    );
    if (!hasAnyChartData) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }
  }

  if (pen.total === 0) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  const gaps = unchartedMarkets(rows, 5);
  const gapItems = gaps.map((cc) => {
    const name = countryName(cc, cc.toUpperCase());
    return `<li>${flagEmoji(cc)} ${name}</li>`;
  });

  const gapsHtml = gaps.length > 0
    ? `<div class="market-gap__gaps">
        <p class="market-gap__gaps-label">${t('marketGap.notCharting')}</p>
        <ul class="market-gap__gap-list">${gapItems.join('')}</ul>
       </div>`
    : `<p class="market-gap__no-gaps">${t('marketGap.allMajorCharting')}</p>`;

  container.innerHTML = `
    <details class="market-gap-details" open>
      <summary class="market-gap-summary">${t('marketGap.title')}</summary>
      <div class="market-gap-body">
        <ul class="market-gap__stats">
          <li>${t('marketGap.free', { n: pen.free, total: pen.total })}</li>
          <li>${t('marketGap.paid', { n: pen.paid, total: pen.total })}</li>
          <li>${t('marketGap.grossing', { n: pen.grossing, total: pen.total })}</li>
        </ul>
        ${gapsHtml}
      </div>
    </details>
  `.trim();

  container.hidden = false;
}
